import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  cn,
} from '@rematter/ui-web';
import { LucideAlertCircle } from 'lucide-react';

export type ConfirmationDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  confirmDisabled?: boolean;
  confirmColor?: 'failure' | 'primary';
  icon?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
};

const defaultIcon = (
  <LucideAlertCircle className="size-5 shrink-0 fill-failure text-white" />
);

export const ConfirmationDialog = ({
  isOpen,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  isLoading = false,
  confirmDisabled = false,
  confirmColor = 'failure',
  icon = defaultIcon,
  className,
  children,
}: ConfirmationDialogProps) => (
  <DialogOverlay
    isOpen={isOpen}
    className={cn('@ConfirmationDialog', className)}
    onOpenChange={onOpenChange}
  >
    <DialogContent className="max-w-md min-w-[330px]" closeButton={false}>
      <DialogHeader className="mr-0">
        <div className="flex items-center gap-2 text-sm font-semibold">
          {icon}
          {title}
        </div>
        <div className="text-sm text-neutral/75">{description}</div>
      </DialogHeader>
      {children && <div className="px-4">{children}</div>}
      <div className="flex justify-end gap-2 px-4 py-4">
        <Button
          variant="text"
          color="neutral"
          onPress={onCancel}
          isDisabled={isLoading}
        >
          {cancelLabel}
        </Button>
        <Button
          variant="solid"
          color={confirmColor}
          onPress={onConfirm}
          isDisabled={confirmDisabled || isLoading}
          isLoading={isLoading}
        >
          {confirmLabel}
        </Button>
      </div>
    </DialogContent>
  </DialogOverlay>
);
